import { postJson } from "./http";
import type { ZohoBankAccountDTO } from "./zohoRecordsApi";

/**
 * Adding a bank account to a client's real Zoho Books — the banks-review
 * screen's one write. The list it reviews comes from zohoRecordsApi.
 */

/** What the add-bank form collects. Everything past the first two is optional. */
export interface NewBankAccount {
  account_name: string;
  /** Zoho makes these two through this endpoint. Cash is not one of them. */
  account_type: "bank" | "credit_card";
  account_number?: string;
  bank_name?: string;
  /** Zoho carries the IFSC as `routing_number`; the backend renames it. */
  ifsc?: string;
  /**
   * One of the organisation's ENABLED currencies, or "" to let Zoho default to
   * the base currency. Worth choosing at create time: Zoho fixes an account's
   * currency once transactions land on it, so a wrong one here means
   * delete-and-recreate in Zoho.
   */
  currency_id?: string;
  /**
   * ZOUR-120: a bank account with no number, bank or IFSC (the backend drops
   * them), flagged on our row so go-live carries it to the tenant. Bank type only.
   */
  is_petty_cash?: boolean;
}

export interface CreatedBankAccountDTO {
  created: { account_id: string; account_name: string };
  /**
   * Set when the account was made but the re-read that follows it failed. The
   * account exists; the list beside it may be a moment behind.
   */
  reread_error: string | null;
  /** The whole list again, re-read from Zoho after the create. */
  bank_accounts: ZohoBankAccountDTO[];
}

interface CreatedBankEnvelope {
  success: boolean;
  data: CreatedBankAccountDTO;
}

/**
 * Add a bank or credit-card account to the client's real Zoho Books.
 *
 * THIS WRITES TO A CUSTOMER'S BOOKS. There is no undo: an account added by
 * mistake has to be deleted in Zoho, and one with transactions against it
 * cannot be deleted at all. Do not call it speculatively or retry it blind; a
 * failure that reached Zoho may still have created the account.
 *
 * No opening balance travels with it. Zoho holds those separately, as one dated
 * set for the organisation, and they get their own step.
 *
 * The readable refusal lives under `errors.bank` — "This client's books
 * already have an account called …", or Zoho's own refusal passed through.
 */
export const createBankAccount = async (
  publicId: string,
  account: NewBankAccount
): Promise<CreatedBankAccountDTO> =>
  (
    await postJson<CreatedBankEnvelope>(
      "/api/onboarding/banks/create",
      { public_id: publicId, ...account },
      ["bank"]
    )
  ).data;

interface PettyCashEnvelope {
  success: boolean;
  data: { bank_accounts: ZohoBankAccountDTO[] };
}

/**
 * Mark or unmark one of the client's accounts as petty cash (ZOUR-120). For a
 * client already keeping petty cash in Zoho as a bank account with no number.
 *
 * Our database only — Zoho has no petty-cash flag and is not called. Bank-type
 * accounts only; the refusal for anything else is under `errors.bank`. Hands
 * back the whole list.
 */
export const setPettyCash = async (
  publicId: string,
  accountId: string,
  isPettyCash: boolean
): Promise<ZohoBankAccountDTO[]> =>
  (
    await postJson<PettyCashEnvelope>(
      "/api/onboarding/banks/petty-cash",
      { public_id: publicId, account_id: accountId, is_petty_cash: isPettyCash },
      ["bank"]
    )
  ).data.bank_accounts;
